import ActionMenu from "@/components/app/ActionMenu";
import DeleteModal from "@/components/app/DeleteModal";
import { useArchiveState } from "@/hooks/useArchiveState";
import { useArchiveEmail } from "@/queries/patient/use-email-archive";
import { useGetPatientPortalEmails } from "@/queries/patient/use-get-all-email";
import { Box } from "@mui/material";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import Flex from "../../../../../components/Flex";
import RenderWithTooltip from "../../../../../components/RenderWithTooltip";
import useDisclosure, {
  DisclosureHost,
} from "../../../../../hooks/use-disclosure";
import { formatDateTime } from "../../../../../utils/date-time-utility";
import {
  DGT_Pagination,
  applyDefaultColumnSettings,
  getXtenantId,
} from "../../../../../utils/utils";
import ViewEmail from "../../patient/patient-list/patient-chart-new/communication/ViewEmail";

// Type for the context passed from CommunicationLayout
interface CommunicationContext {
  sendMailDisclosure: DisclosureHost;
  rowSelectionModel: GridRowSelectionModel;
  setRowSelectionModel: (model: GridRowSelectionModel) => void;
  pagination: DGT_Pagination;
  setPagination: (pagination: DGT_Pagination) => void;
  // patientList: PaginatedContent<any>;
  patientListLoading: boolean;
  patientListRefetching: boolean;
  isArcheivedEmail: boolean;
}

const EmailTable = () => {
  // Get email filters from context
  const {
    rowSelectionModel,
    setRowSelectionModel,
    pagination,
    setPagination,
    patientListLoading,
    patientListRefetching,
    isArcheivedEmail,
  } = useOutletContext<CommunicationContext>();

  const {
    data: emailList,
    refetch,
    isLoading,
  } = useGetPatientPortalEmails({
    pagination,
    xTENANTID: getXtenantId(),
    archive: isArcheivedEmail,
  });

  const viewEmailDisclosureHost = useDisclosure();
  const { mutateAsync: archiveMutation } = useArchiveEmail();
  const { deleteDisc, archiveHandler } = useArchiveState(archiveMutation);
  const [archiveEmail, setArchiveEmail] = useState<boolean>(false);

  const COLUMNS = applyDefaultColumnSettings<any>([
    {
      field: "sentBy",
      flex: 0.3,
      minWidth: 100,
      disableColumnMenu: true,
      headerName: "Sent By",
      renderCell: ({ value }) => <RenderWithTooltip value={value} />,
    },
    {
      field: "subject",
      headerName: "Subject",
      flex: 0.3,
      minWidth: 100,
      renderCell: ({ value }) => <RenderWithTooltip value={value} />,
    },
    {
      field: "sendDate",
      flex: 0.5,
      minWidth: 150,
      headerName: "Sent Date & Time",
      renderCell: ({ row }) => (
        <RenderWithTooltip
          value={row?.sendDate ? formatDateTime(row.sendDate) : "-"}
        />
      ),
    },
    {
      field: "Action",
      headerName: "Actions",
      minWidth: 90,
      hideSortIcons: true,
      renderCell: ({ row }) => (
        <ActionMenu
          options={[
            { value: "view", label: "View" },
            { value: "archive", label: "Archive" },
          ]}
          onActionClick={(v) => {
            if (v === "view") {
              viewEmailDisclosureHost.open(row);
            }
            if (v === "archive") {
              setArchiveEmail(row?.archive);
              deleteDisc.open(row?.uuid);
            }
          }}
        />
      ),
    },
  ]);

  return (
    <Flex flexDirection={"column"} width={"100%"} height={"100%"}>
      <ViewEmail title="View Email" disclosureHost={viewEmailDisclosureHost} />

      <DeleteModal
        w="380px"
        open={deleteDisc.opened}
        onClose={deleteDisc.close}
        id={deleteDisc.deleteId}
        onConfirmation={(id) =>
          archiveHandler(!archiveEmail, id).then((res) => {
            refetch();
            return res;
          })
        }
        content={`You want to Archive this email`}
      />

      <Box flex={1} overflow="auto">
        <CustomDataGridV2
          rows={emailList?.data?.content ?? []}
          getRowId={(row) => row?.uuid}
          rowCount={emailList?.data?.page?.totalElements}
          loading={patientListLoading || patientListRefetching || isLoading}
          checkboxSelection
          disableRowSelectionOnClick
          columns={COLUMNS}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={setRowSelectionModel}
        />
      </Box>
    </Flex>
  );
};

export default EmailTable;
